/**
* @Description This is FasatDropdown component page.
* This is a wrapper on "Dropdown" component from MUX without adding any custom functionlities
* @FileName fasatDropdown.js
* @Author UMA DUTTA-duttuma
* @CreatedOn 29 January, 2021 6:00:00
* @IssueID CDTFASATMOD- 502
*/
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes, { bool } from 'prop-types';
import { Dropdown } from '@manulife/mux';
import { Dropdown as CustomDropdown } from '../../libs/fasatSharedComponent';
import './fasatDropdown.scss';
import FasatFormErrors from '../fasatFormErrors/fasatFormErrors';
import { staticCommonLabelKeys } from '../../moduleConstants';

const setError = (name, id) => (name || id);
const validateDropdownType = ({
  isMultiselect, searchEnabled, forcible }) => (isMultiselect || searchEnabled || forcible);
const setComponentClassName = ({ inputClass, errors, fasatInputErrorClass }) => (`${inputClass} ${
  errors.length > 0 && errors[0].isError ? fasatInputErrorClass : ''
}`);
function FasatDropdown(props) {
  // NOTE:
  // Avoid spreading the rest props into MUX Dropdown components.
  // Formik's getFieldProps (e.g., onBlur/required-related handlers) were being
  // forwarded via the rest parameter, which caused MUX to show "Required" on blur.
  // To prevent MUX's built-in blur validation from being triggered, we explicitly
  // pass only supported props (id, label, value) instead of spreading.
  const { isMultiselect, forcible, searchEnabled, name, onHandleChange, placeholder,
    errors, dropdownItems, filterCriteria, selectLabelEnabled, 'data-testid': dataTestId,
    ariaLabel, 'aria-label': ariaLabelProp, label, value, disabled } = props;
  const { id } = props;
  const accessibilityLabel = ariaLabel || ariaLabelProp;
  const errorName = setError(name, id);
  const inputClass = 'fasatDropdown';
  const fasatInputErrorClass = 'withError';
  const [t] = useTranslation();
  const selectLabel = t(staticCommonLabelKeys.SELECT_DROPDOWN_LABEL);
  const selectLabelOption = {
    label: selectLabel,
    value: '',
  };
  const generateFasatList = () => {
    if (filterCriteria) {
      return dropdownItems.filter((item) => item.label !== 'All');
    }
    if (selectLabelEnabled) {
      if (!dropdownItems.find((item) => item.label === selectLabel)) {
        dropdownItems.splice(0, 0, selectLabelOption);
      }
    } else {
      return dropdownItems?.filter((item) => item.label !== selectLabel);
    }
    return dropdownItems;
  };
  useEffect(() => {
    const svgTitles = document.querySelectorAll('[data-testid="dropdown-native-icon"] svg title');
    const svgTitlesArr = [].slice.call(svgTitles);
    svgTitlesArr.forEach((title) => {
      if (title.innerHTML === '') {
        title.remove();
      }
    });
  });
  if (validateDropdownType({ isMultiselect, searchEnabled, forcible })) {
    // Issue context (Group By): duplicate column selection was retained visually
    // even after state cleared in `groupByRow.jsx`. Using `value || ''` keeps the
    // dropdown controlled and forces a proper visual reset on clear.
    return (
      <div className={setComponentClassName({ inputClass, errors, fasatInputErrorClass })}>
        <CustomDropdown
          id={id}
          label={label}
          value={value || ''}
          onChange={onHandleChange}
          isMultiselect={isMultiselect}
          placeholder={placeholder}
          searchEnabled={searchEnabled}
          dropdownItems={dropdownItems}
          data-testid={dataTestId}
          aria-label={accessibilityLabel}
          disabled={disabled}
        />
        <FasatFormErrors name={errorName} errors={errors} />
      </div>
    );
  }
  // Issue context (Group By): same as above for the non-search/non-multiselect path.
  // The controlled fallback `value || ''` ensures clearing selection resets UI.
  return (
    <div
      className={`${inputClass} ${
        errors.length > 0 && errors[0].isError ? fasatInputErrorClass : ''
      }`}
    >
      <Dropdown
        id={id}
        label={label}
        value={value || ''}
        onChange={onHandleChange}
        customStyle={{
          rootStyle: { width: '100%' },
        }}
        placeholder={!selectLabelEnabled ? placeholder : ''}
        dropdownItems={generateFasatList()}
        data-testid={dataTestId}
        aria-label={accessibilityLabel}
        disabled={disabled}
      />
      <FasatFormErrors name={errorName} errors={errors} />
    </div>
  );
}

FasatDropdown.propTypes = {
  isMultiselect: PropTypes.bool,
  searchEnabled: PropTypes.bool,
  forcible: PropTypes.bool,
  name: PropTypes.string,
  id: PropTypes.string,
  selectLabelEnabled: bool,
  placeholder: PropTypes.string,
  onHandleChange: PropTypes.func,
  ariaLabel: PropTypes.string,
  'aria-label': PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.shape(
    {
      id: PropTypes.string,
      isError: PropTypes.bool,
      msg: PropTypes.string,
    },
  )),
};

FasatDropdown.defaultProps = {
  isMultiselect: false,
  searchEnabled: false,
  selectLabelEnabled: false,
  forcible: false,
  onHandleChange: () => undefined,
  placeholder: 'Select',
  name: '',
  id: '',
  errors:
     [{
       id: '',
       isError: false,
       msg: '',
     }],
};

export default FasatDropdown;
